import React, { createRef, useState, useEffect } from 'react';
import readXlsxFile, { readSheetNames } from 'read-excel-file';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import { TreeTable } from 'components/TreeTable';
import LinearProgress from '@material-ui/core/LinearProgress';
import { genColumns as excelColumnNamesGenerator } from 'excel-like-column-name-generator';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import {
  makeStyles,
  // TableContainer,
  // Table,
  // TableRow,
  // TableCell,
  // TableHead,
  Grid,
  Button,
  Typography,
  IconButton,
} from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import {
  CommonDialog,
  FormField2 as Field,
} from 'components';
import api from 'utils/api';
import {
  b64toBlob,
  blobToFileDownload,
  toast,
} from 'utils/helpers';

const useStyles = makeStyles({
  root: {},
  footerWrap: {
    marginTop: 40,
  },
  viewWrap: {
    // height: '600px',
    // overflow: 'auto',
  }
})

const AUTO_MAP = {
  sam: 'estimate',
  sd: 'part',
  sk: 'chapter',
  psk: 'subchapter',
  elg: 'group',
  el: 'element',
  ikn: 'rate',
  ds: 'resource-work',
  med: 'resource-material',
  mech: 'resource-mechanism',
  ir: 'resource-equipment',
  iknds: 'rate-work',
  iknmed: 'rate-material',
  iknmech: 'rate-mechanism',
  iknir: 'rate-equipment',
};

const VALID_ROW_TYPES = [
  'estimate',
  'part',
  'chapter',
  'subchapter',
  'group',
  'element',
  'rate',
  'resource',
  'resource-work',
  'resource-material',
  'resource-mechanism',
  'resource-equipment',
  'rate-work',
  'rate-material',
  'rate-mechanism',
  'rate-equipment'
];

const parseTitleCoefficients = (rawTitle) => {
  if (!rawTitle) return { coefficients: null, cleanTitle: rawTitle };

  const parseCoef = (key) => {
    const match = String(rawTitle).match(new RegExp(`(?:^|[^a-zA-Z0-9])${key}\\s*=\\s*(\\d+(?:[.,]\\d+)?|[.,]\\d+)`, 'i'));
    return match ? parseFloat(match[1].replace(',', '.')) : undefined;
  };

  const keys = ['k1', 'k2', 'k3', 'k4', 'k8', 'k9'];
  const found = [];
  let cleanK8K9FromTitle = false;
  keys.forEach(k => {
    const val = parseCoef(k);
    if (val !== undefined && !isNaN(val)) {
      if(k != 'k8' && k != 'k9') {
        found.push(`${k}=${val}`);
      } else {
        cleanK8K9FromTitle = true;
      }
    }
  });

  if (!found.length && !cleanK8K9FromTitle) {
    return { coefficients: null, cleanTitle: rawTitle };
  }

  const coefficients = found.join(';');

  let cleanTitle = String(rawTitle)
    .replace(/\s*[\(\[]\s*(?:[kK](?:[1-4]|8|9)\s*=\s*(?:\d+(?:[.,]\d+)?|[.,]\d+)[\s,;]*)+\s*[\)\]]/g, '')
    .replace(/\b[kK](?:[1-4]|8|9)\s*=\s*(?:\d+(?:[.,]\d+)?|[.,]\d+)/g, '')
    .replace(/[\(\[]\s*[,;]*\s*[\)\]]/g, '')
    .replace(/\s*([,;])(?:\s*[,;])+/g, '$1')
    .replace(/\s+([,;])/g, '$1')
    .replace(/[,;\s]+([\s\)\]])/g, '$1')
    .replace(/\s+/g, ' ')
    .replace(/^[\s,;.-]+|[\s,;.-]+$/g, '')
    .trim();

  cleanTitle = cleanTitle || rawTitle;

  return { coefficients, cleanTitle };
};

const RatesImportModal = ({ version, objectId, itemId, onClose, topLevelType = 'estimate', createEstimate = false, projectId, fromMedia = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const fileRef = createRef();
  const [file, setFile] = useState();
  const [record, setRecord] = useState({ columnNames: 'first-row' });
  const [headers, setHeaders] = useState();
  const [firstRow, setFirstRow] = useState();
  const [rows, setRows] = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState();
  const [allSelected, setAllSelected] = useState(false);
  const [allLocked, setAllLocked] = useState(false);
  const [fillColumnData, setFillColumnData] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!file) return;

    setHeaders(null);

    if (fromMedia) {
      fetchFromMediaFile();
    } else {
      fetchFromExcelFile();
    }
  }, [file]);

  useEffect(() => {
    const availableRows = hasFirstRow ? _.tail(result) : result;
    const isAllSelected = availableRows?.filter(r => r?.selected)?.length === availableRows?.length;
    const isAllLocked = availableRows?.filter(r => r?.locked)?.length === availableRows?.length;

    setAllSelected(isAllSelected);
    setAllLocked(isAllLocked);
  }, [result, , rows]);

  if (!!createEstimate && !!projectId) {
    useEffect(() => {
      predictColumns();
    }, [headers]);
  }

  const fetchFromMediaFile = async () => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post('/estimates/media-to-table', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!data || !data.length) {
        window.alert('Nepavyko rasti duomenų pasirinktame dokumente');
        setFile(null);
      } else {
        const row = data[0].map((value, idx) => ({ value: idx.toString(), label: value }));
        setFirstRow(row);
        setHeaders(row);
        setRows(data);
      }
    } catch (err) {
      console.error(err);
      window.alert('Nepavyko apdoroti pasirinkto dokumento');
      setFile(null);
    }

    setLoading(false);
  };

  const fetchFromExcelFile = () => {
    readXlsxFile(file).then((excelRows) => {
      if (!excelRows.length) return window.alert(t('Nėra duomenų'));
      const row = excelRows[0].map((value, idx) => ({ value: idx.toString(), label: value }));
      setFirstRow(row);
      setHeaders(row);
      setRows(excelRows);
    });

    readSheetNames(file).then((sheetNames) => {
      setSheetNames(sheetNames);
      setSelectedSheet(sheetNames[0]);
    });
  };

  const toggleSelectAll = (checked) => {
    setResult(result?.map(item => ({ ...item, selected: checked })));
  };

  const toggleLockAll = (checked) => {
    setResult(result?.map(item => ({ ...item, locked: checked })));
  };

  const handleClose = (hadChanges = false) => {
    if (fromMedia && loading) {
      if (!window.confirm(t('Dokumentas vis dar apdorojamas. Ar tikrai norite nutraukti procesą?'))) {
        return;
      }
    }

    onClose(hadChanges);
  };

  const onFileChange = e => {
    const file = e.target.files[0];
    setFile(file);
  };

  const handleRowfieldChange = (rowIdx, field, value) => {
    const newResult = [...result];
    newResult[rowIdx][field] = value;

    setResult(newResult);
  }

  const handleMappingChange = ({ name, value }) => {
    const newRecord = { ...record, [name]: value };

    if (value === '') {
      delete newRecord[name];
    } else {
      if (name === 'columnNames' && value === 'first-row') {
        setHeaders(firstRow);
      } else if (name === 'columnNames' && value === 'none') {
        const row = excelColumnNamesGenerator(firstRow.length).map((value, idx) => ({ value: idx.toString(), label: value }))
        setHeaders(row);
      }
    }

    setRecord(newRecord);

    setResult(rows.map((rowValues, idx) => {
      let type = newRecord?.type ? rowValues[newRecord.type] : null;
      let selected = !result ? true : !!result?.[idx]?.selected;
      let locked = !result ? false : !!result?.[idx]?.locked;
      let added = !result ? false : !!result?.[idx]?.added;
      if (added) {
        const item = {
          key: idx,
          ...result?.[idx]
        }
        return item;
      }

      if (name === 'type') {
        // Attempt to auto select values
        try {
          type = AUTO_MAP[rowValues[value]?.toLowerCase()] || type;
          if (AUTO_MAP[rowValues[value]?.toLowerCase()]) {
            selected = true;
            locked = true;
          } else selected = false;
        } catch (err) {
          console.error('Error auto-mapping type:', err);
        }
      } else {
        type = result?.[idx]?.type || type;
      }

      // Only preserve user-edited values (not default-mapped)
      const prev = result?.[idx] || {};
      const prevMapping = (oldRecord => columnsRaw.reduce((acc, column) => {
        acc[column.key] = rowValues[oldRecord?.[column.key]];
        return acc;
      }, {}))(record); // record before newRecord

      const item = {
        key: idx,
        selected,
        type,
        locked,
        ...columnsRaw.reduce((current, column) => {
          const prevVal = prev[column.key];
          const prevMappedVal = prevMapping[column.key];
          const mappedVal = rowValues[newRecord[column.key]];
          // If user changed the value and it is not empty, preserve it; if deleted (empty), use new mapping
          const isCustom = prevVal !== undefined && prevVal !== prevMappedVal && prevVal !== '' && prevVal !== null;
          return { ...current, [column.key]: isCustom ? prevVal : mappedVal };
        }, {}),
      };

      if (name === 'title') {
        const rawTitle = value !== '' && value !== undefined && value !== null ? rowValues[value] : '';
        if (rawTitle) {
          const { coefficients, cleanTitle } = parseTitleCoefficients(rawTitle);
          item.title = cleanTitle;
          if (coefficients) {
            item.modifiers = newRecord.modifiers && rowValues[newRecord.modifiers]
              ? `${rowValues[newRecord.modifiers]};${coefficients}`
              : coefficients;
          } else if (!newRecord.modifiers) {
            item.modifiers = '';
          }
        } else {
          item.title = '';
          if (!newRecord.modifiers) {
            item.modifiers = '';
          }
        }
      }

      return item;
    }));
  }

  const predictColumns = () => {
    let values = [];
    if (!headers) return;
    headers?.forEach(header => {
      const label = header.label.toLowerCase();
      if (['kodas', 'code'].includes(label)) values.push({ code: header.value });
      else if (['pavadinimas', 'title'].includes(label)) values.push({ title: header.value });
      else if (['mato vnt.', 'unit'].includes(label)) values.push({ unit: header.value });
      else if (['norma', 'norm'].includes(label)) values.push({ norm: header.value });
      else if (['kiekis', 'quantity'].includes(label)) values.push({ quantity: header.value });
      else if (['kaina', 'price'].includes(label)) values.push({ price: header.value });
      else if (['suma', 'sum'].includes(label)) values.push({ amount: header.value });
      else if (['papildoma info', 'modifiers', 'modifikatoriai'].includes(label)) values.push({ modifiers: header.value });
      else if (['tipas', 'type'].includes(label)) values.push({ type: header?.value.toLowerCase() });
      else if (['darbo sudėtis', 'work composition'].includes(label)) values.push({ workComposition: header.value });
    });

    if (Object.keys(record).length == 1) {
      let newRecord = { ...record };
      _.forEach(values, (val, idx) => {
        let name = Object.keys(val)[0];
        let value = val[name];
        newRecord[name] = value;
      });
      setRecord(newRecord);

      let items = [];
      setResult(rows.map((rowValues, idx) => {
        let itemValues = {};
        let type = null;
        let selected = true;
        let locked = true;

        _.forEach(values, (val) => {
          let name = Object.keys(val)[0];
          let value = val[name];

          if (name === 'type') {
            // Attempt to auto select values
            type = AUTO_MAP[rowValues[value]?.toLowerCase()] || type;
            itemValues[name] = type;
            if (AUTO_MAP[rowValues[value]?.toLowerCase()]) {
              selected = true;
              locked = true;
            } else selected = false;
          } else {
            itemValues[name] = rowValues[value];
          }
        });

        if (itemValues.title) {
          const { coefficients, cleanTitle } = parseTitleCoefficients(itemValues.title);
          itemValues.title = cleanTitle;
          if (coefficients && !itemValues.modifiers) {
            itemValues.modifiers = coefficients;
          } else if (coefficients && itemValues.modifiers) {
            itemValues.modifiers = `${itemValues.modifiers};${coefficients}`;
          }
        }

        const item = {
          key: idx,
          selected,
          type,
          locked,
          ...columnsRaw.reduce((current, column) => {

            return { ...current, ...itemValues };
          }, {}),
        };

        items.push(item);
        return item;
      }));
    }
  }

  const handleSheetChange = (sheet) => {
    setHeaders(null);
    setRecord(null);
    setResult(null);
    setSelectedSheet(sheet.value);

    readXlsxFile(file, { sheet: sheet.value }).then((excelRows) => {
      if (!excelRows.length) return window.alert(t('Nėra duomenų'));

      const row = excelRows[0].map((value, idx) => ({ value: idx.toString(), label: value }));
      setFirstRow(row);
      setHeaders(row);
      setRows(excelRows);
    });
  }

  const correctResources = (items) => {
    let payload = items.map(item => {
      if (item.type.includes('resource')) {
        item.resourceType = item.type.split("-")[1];
        item.type = 'resource';
      }
      return item;
    });

    return payload;
  }

  const getExcelVersion = (items) => {
    let version = '';
    items.map(item => {
      if (item.modifiers != null) {
        let modifiers = item.modifiers.split(";");

        modifiers.map(mod => {
          if (mod.toLowerCase().includes('kainos')) {
            let versionCode = mod.match(/\d/g).join("");

            version = versionCode;
            return version;
          }
        });
      }
    })

    return version;
  }

  const applyModifiers = (items) => {
    let payload = items.map(item => {
      if (item.modifiers != null) {
        let modifiers = item.modifiers.split(";");
        let updatedSettings = [];
        modifiers.map(mod => {
          if (!mod.toLowerCase().includes('kainos')) {
            const coef = mod.split("=");
            if (['k1', 'k2', 'k3', 'k4'].includes(coef[0].toLowerCase())) {
              updatedSettings.push({ code: coef[0].toLowerCase(), value: coef[1] });
            } else {
              updatedSettings.push({ code: coef[0].toLowerCase(), unit: 'percent', value: coef[1] });
            }
          }
        })

        if (updatedSettings.length) item.updatedSettings = updatedSettings;
      }
      return item;
    });

    return payload;
  }


  const emptyTypeRowCheck = (items) => {
    let payload = items.filter(item => item.selected && item.key != 0 &&
      !VALID_ROW_TYPES.includes(item.type)
    );

    return !!payload.length;
  }

  const handleSubmit = async () => {
    const hasFirstRow = record?.columnNames === 'first-row';
    const basePayload = (hasFirstRow ? _.tail(result) : result).filter(r => r.selected);
    const emptyTypeRows = emptyTypeRowCheck(basePayload);

    if (emptyTypeRows) {
      window.alert(t('Pažymėtos eilutės neturinčios tipo'));
      return;
    }

    const excelVersion = getExcelVersion(basePayload);
    if (excelVersion && version.code != excelVersion) {
      window.alert(`${t('Skaičiavimo objekto ir įkeliamų objektų kainynų versijos nesutampa')}.`);
      return;
    }

    let payload = correctResources(basePayload);
    payload = applyModifiers(payload);

    if (!payload.length) {
      window.alert(t('Nepažymėta nei viena eilutė'));
      return;
    }

    setLoading(true);

    try {
      let query = '';
      if (fillColumnData) query = query.concat('?importEmpty=1');

      // set estimate to read only while importing
      await api.post(`/estimates/${objectId}/readonly`, { isReadonly: true });

      api.post(`/estimates/${objectId}/items/${itemId}/import-rates${query}`, payload);
      toast(t('Duomenys importuojami, prašome palaukti'), 'success');

      setTimeout(() => {
        window.dispatchEvent(new Event('fetchAndOpenNotifications'));
      }, 500);

      // direct to project dashboard
      history.push(`/projects/${projectId}/dashboard`);
      handleClose(true);
    } catch (err) {
      console.log('Import failed', err);
      window.alert('Nepavyko įkelti');
      handleClose();
    }

    setLoading(false);
  };

  const handleNewEstimateSubmit = async () => {
    const hasFirstRow = record?.columnNames === 'first-row';
    const basePayload = (hasFirstRow ? _.tail(result) : result).filter(r => r.selected);
    const emptyTypeRows = emptyTypeRowCheck(basePayload);

    if (emptyTypeRows) {
      window.alert(t('Pažymėtos eilutės neturinčios tipo'));
      return;
    }

    const excelVersion = getExcelVersion(basePayload);

    let payload = correctResources(basePayload);
    payload = applyModifiers(payload);

    if (!payload.length) {
      window.alert(t('Nepažymėta nei viena eilutė'));
      return;
    }

    setLoading(true);

    try {
      api.post(`/estimates/${projectId}/import-rates`, { data: payload, version: excelVersion || null, estimateName: file?.name.substring(0, file?.name.lastIndexOf('.')) });
      toast(t('Duomenys importuojami, prašome palaukti'), 'success');

      setTimeout(() => {
        window.dispatchEvent(new Event('fetchAndOpenNotifications'));
      }, 500);

      history.push(`/projects/${projectId}/dashboard`);
      handleClose(true);
    } catch (err) {
      console.log('Import failed', err);
      window.alert('Nepavyko įkelti');
      handleClose();
    }

    setLoading(false);
  };

  const handleSelectedRowExcelDownload = async () => {
    const hasFirstRow = record?.columnNames === 'first-row';
    const selectedRowIndexesFromExcel = [];
    (hasFirstRow ? _.tail(result) : result).filter((r, idx) => r.selected ? selectedRowIndexesFromExcel.push(idx) : null);

    const TYPES_MAP = {
      estimate: 'sam',
      part: 'sd',
      chapter: 'sk',
      subchapter: 'psk',
      group: 'elg',
      element: 'el',
      rate: 'ikn',
    };
    const RESOURCE_TYPES_MAP = {
      work: 'ds',
      material: 'med',
      mechanism: 'mech',
      equipment: 'ir',
    }

    const selectedRows = _.map(hasFirstRow ? _.tail(rows) : rows, (row, index) => {
      const idx = hasFirstRow ? index + 1 : index;

      if (!selectedRowIndexesFromExcel.includes(index)) {
        return row
      } else {
        const rowType = _.get(result[idx], 'type');
        let typeCode;

        if (rowType != null && rowType.includes('resource')) {
          const resourceType = rowType.split("-")[1];
          typeCode = RESOURCE_TYPES_MAP[resourceType];
        } else {
          if (TYPES_MAP[rowType.toLowerCase()]) {
            typeCode = TYPES_MAP[rowType.toLowerCase()];
          } else typeCode = null;
        }

        return [...rows[idx], typeCode];
      }
    });

    setLoading(true);

    try {
      const data = { headers: hasFirstRow ? [...headers, { value: headers.length.toString(), label: t('Tipas').toLowerCase() }] : null, rows: selectedRows };
      const response = await api.post(`/estimates/${objectId}/items/${itemId}/generate-rates-excel`, data);

      const fileName = file.name.substring(0, file.name.lastIndexOf(".")) + "_BG.xlsx";
      downloadFile(fileName, response.data);
    } catch (err) {
      console.log('Download failed', err);
      window.alert('Nepavyko atsisiųsti');
    }

    setLoading(false);
  };

  const downloadFile = (name, base64Data) => {
    const blob = b64toBlob(base64Data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

    blobToFileDownload(name, blob);
  }

  const handleMassTypeChange = (value) => {
    const newResult = [...result];
    newResult.forEach(r => !r.locked ? r.type = value : null);

    setResult(newResult);
  }

  const handleGetRowTypes = async () => {
    const newResult = [...result];
    const codes = [];

    _.map(newResult, row => {
      if (row.selected && row.code) codes.push(row.code);
    });

    const { data } = await api.post(`/estimates/${objectId}/get-row-types`, { codes });

    _.map(newResult, row => {
      if (row.selected && row.code) {
        const item = _.find(data, o => {
          if (o && row.code) return o.code.toLowerCase() == row.code.toString().toLowerCase();
        });

        if (item && item.type) {
          if (item.type != 'rate') {
            row.type = item.type;
            row.resourceType = item.type.split('-')[1];
          }
          if (item.type == 'rate') row.type = item.type;
          row.locked = true;
        }
      }
    });

    setResult(newResult);
  }

  const renderSelect = ({ name, value, onChange, options, style, disabled }) => (
    <select
      style={style || { width: '100%' }}
      onChange={onChange}
      value={value}
      name={name}
      disabled={disabled}
    >
      <option value="" />
      {options?.map((field) => (
        <option key={field.value} value={field.value}>{field.label}</option>
      ))}
    </select>
  )

  const renderMediaProgressBar = () => {

    return (
      <div style={{ textAlign: 'center' }}>
        <LinearProgress />
        <Typography variant="body2" style={{ marginTop: 5 }}>
          {t('Dokumento analizė gali užtrukti kelias minutes. Neuždarykite lango ir neperkraukite puslapio.')}
        </Typography>
      </div>
    )
  };

  const handleCellChange = (dataIndex, record, newValue) => {
    // Find the row by its key (or another unique identifier)
    const idx = result.findIndex(r => r.key === record.key);
    if (idx === -1) return;

    // Create a new array with the updated value
    const newResult = [...result];
    newResult[idx] = { ...newResult[idx], [dataIndex]: newValue };

    setResult(newResult);
  };

  const rowTypes = [
    { key: 'SAM', value: 'estimate', label: t('Sąmata'), allowedFor: ['estimate'] },
    { key: 'SD', value: 'part', label: t('Sąmatos dalis'), allowedFor: ['estimate'] },
    { key: 'SK', value: 'chapter', label: t('Skyrius'), allowedFor: ['estimate', 'part'] },
    { key: 'PSK', value: 'subchapter', label: t('Poskyris'), allowedFor: ['estimate', 'part', 'chapter'] },
    { key: 'ELG', value: 'group', label: t('El. grupė'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter'] },
    { key: 'EL', value: 'element', label: t('Elementas'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter', 'group'] },
    { key: 'IKN', value: 'rate', label: t('Įkainis'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'DS', value: 'resource-work', resourceType: 'work', label: t('Darbas'), allowedFor: ['rate', 'estimate', 'part', 'chapter', 'subchapter', 'group', 'element', 'resource-work'] },
    { key: 'MED', value: 'resource-material', resourceType: 'material', label: t('Medžiaga'), allowedFor: ['rate', 'estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'MECH', value: 'resource-mechanism', resourceType: 'mechanism', label: t('Mechanizmas'), allowedFor: ['rate', 'estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'IR', value: 'resource-equipment', resourceType: 'equipment', label: t('Įrenginys'), allowedFor: ['rate', 'estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'IKNDS', value: 'rate-work', label: t('Įk. darbas'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'IKNMED', value: 'rate-material', label: t('Įk. medžiaga'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'IKNMECH', value: 'rate-mechanism', label: t('Įk. mechanizmas'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
    { key: 'IKNIR', value: 'rate-equipment', label: t('Įk. įrenginys'), allowedFor: ['estimate', 'part', 'chapter', 'subchapter', 'group', 'element'] },
  ].filter(item => item.allowedFor.includes(topLevelType));

  const columnsRaw = [
    { key: 'code', label: t('Kodas') },
    { key: 'title', label: t('Pavadinimas'), width: 300 },
    { key: 'unit', label: t('Mat. vnt.'), width: 80 },
    { key: 'norm', label: t('Norma'), type: 'number', width: 80 },
    { key: 'quantity', label: t('Kiekis'), type: 'number' },
    { key: 'price', label: t('Kaina'), type: 'number' },
    { key: 'amount', label: t('Suma'), type: 'number' },
    { key: 'modifiers', label: t('Papildoma informacija'), width: 140 },
    { key: 'workComposition', label: t('Darbų sudėtis') },
  ];

  const columns = [
    {
      dataIndex: 'selected',
      key: 'selected',
      width: 40,
      title: (
        <div style={{ textAlign: 'center' }}>
          <input
            type="checkbox"
            checked={allSelected}
            style={{ verticalAlign: 'middle' }}
            onChange={(e) => toggleSelectAll(e.currentTarget.checked)}
          />
        </div>
      ),
      render: (value, item) => {

        return (
          <div style={{ textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={value}
              style={{ verticalAlign: 'middle' }}
              onChange={(e) => handleRowfieldChange(item.key, 'selected', e.currentTarget.checked)}
            />
          </div>
        )
      }
    },
    ...columnsRaw.map((col) => ({
      title: (
        <>
          {col.label}
          {renderSelect({
            name: col.key,
            value: record?.[col.key],
            onChange: (e) => handleMappingChange({ name: col.key, value: e.target.value }),
            options: headers,
          })}
        </>
      ),
      dataIndex: col.key,
      key: col.key,
      width: col.width || 100,
      editable: true,
      type: col.type,
      render: (text) => <span className="ellipsis" title={text}>{text}</span>
    })),
    {
      title: (
        <>
          {t('Tipas')}
          {renderSelect({
            name: 'type',
            value: record?.type,
            onChange: (e) => handleMappingChange({ name: 'type', value: e.target.value }),
            options: headers || [],
          })}
        </>
      ),
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (text, item) => {

        return renderSelect({
          name: 'type',
          value: item?.type,
          onChange: (e) => handleRowfieldChange(item.key, 'type', e.target.value),
          options: rowTypes,
        })
      }
    },
    {
      dataIndex: 'locked',
      key: 'locked',
      width: 55,
      title: (
        <div style={{ textAlign: 'center' }}>
          {`${t('Išlaikyti tipą')} `}
          <input
            type="checkbox"
            checked={allLocked}
            style={{ verticalAlign: 'middle' }}
            onChange={(e) => toggleLockAll(e.currentTarget.checked)}
          />
        </div>
      ),
      render: (value, item) => {

        return (
          <div style={{ textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={value}
              style={{ verticalAlign: 'middle' }}
              onChange={(e) => handleRowfieldChange(item.key, 'locked', e.currentTarget.checked)}
            />
          </div>
        )
      }
    },
    {
      dataIndex: 'insertRow',
      key: 'insertRow',
      width: 40,
      title: (
        <>
          {t('Įterpti eilutę')}
        </>
      ),
      render: (value, item) => {

        return (
          <div style={{ textAlign: 'center' }}>
            <IconButton
              aria-label="add-row"
              color="primary"
              size="small"
              onClick={() => handleAdd(item.key)}
            >
              <AddBoxIcon />
            </IconButton>
          </div>
        )
      }
    },
  ];

  const hasMappings = !!Object.keys(_.pick(record, ['code', 'title'])).length;
  const hasFirstRow = record?.columnNames === 'first-row';

  const data = hasMappings ? (hasFirstRow ? _.tail(result) : result) : [];

  const handleAdd = (index = null) => {
    const newData = {
      key: index + 1,
      selected: true,
      type: null,
      locked: false,
      added: true,
      code: '',
    };

    if (index == result.length - 1 || index == null) {
      setResult([...result, newData]);
      setRows([...rows, new Array(12).fill('')]);
    } else {
      const preIndexResult = result.slice(0, index + 1);
      const postIndexResult = result.slice(index + 1);
      _.forEach(postIndexResult, (row) => {
        row.key++;
      });
      const updatedResult = [...preIndexResult, newData, ...postIndexResult];
      setResult(updatedResult);

      const preIndexRows = rows.slice(0, index + 1);
      const postIndexRows = rows.slice(index + 1);
      const updatedRows = [...preIndexRows, new Array(12).fill(''), ...postIndexRows];
      setRows(updatedRows);
    }
  };

  return (
    <CommonDialog
      visible
      onClose={handleClose}
      maxWidth={!file || (loading && fromMedia) ? 'sm' : 'xl'}
      style={{ position: 'relative' }}
      title={fromMedia ? t('Įkainių įkėlimas iš PDF arba nuotraukos') : t('Įkainių įkėlimas iš Excel')}
      loading={loading}
    >
      <div className={classes.viewWrap}>
        <input
          type="file"
          ref={fileRef}
          onChange={onFileChange}
          accept={fromMedia ? 'application/pdf, image/jpeg, image/png, image/webp' : '.xlsx'}
          style={{ display: 'none' }}
        />

        {!file && (
          <div style={{ textAlign: 'center' }}>
            <Button color="primary" variant="contained" onClick={() => fileRef.current.click()}>
              {fromMedia ? t('Pasirinkti PDF dokumentą arba nuotrauką') : t('Pasirinkti Excel dokumentą')}
            </Button>
          </div>
        )}

        {fromMedia && loading && !rows.length && renderMediaProgressBar()}

        {!!headers && (
          <>
            <div style={{ margin: '0px auto' }}>
              <Grid
                container
                spacing={2}
              >
                {sheetNames.length > 1 ? (
                  <Grid item xs={2}>
                    <Field
                      label={t('Excel lapai')}
                      type="select"
                      options={_.map(sheetNames, sheet => {
                        return { value: sheet, label: sheet }
                      })}
                      name="sheetNames"
                      onChange={handleSheetChange}
                      value={selectedSheet}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={4}>
                  <Field
                    label={t('Laukų pavadinimai')}
                    type="select"
                    options={[
                      { value: 'first-row', label: t('Pirmoje eilutėje') },
                      { value: 'none', label: t('Nėra (įvardinti raidėmis A...Z)') },
                    ]}
                    name="columnNames"
                    onChange={handleMappingChange}
                    value={record?.columnNames}
                  />
                </Grid>
              </Grid>

              <h4 style={{ marginTop: 10, marginBottom: 0 }}>{t('Excel stulpelių reikšmės')}</h4>

              <div className="rates-import-table">
                <TreeTable
                  onCellChange={handleCellChange}
                  columns={columns}
                  data={data}
                  locale={{ emptyText: <div style={{ padding: 30 }}>{t('Nėra duomenų')}</div> }}
                  scroll={{ y: 400 }}
                  footer={() => (!createEstimate &&
                    <div style={{ textAlign: 'right', display: 'inline-flex', justifyContent: 'flex-end', width: '100%' }}>
                      <div style={{ textAlign: 'center', paddingRight: 12 }}>
                        <input
                          type="checkbox"
                          checked={fillColumnData}
                          style={{ verticalAlign: 'middle', marginRight: 6 }}
                          onChange={(e) => setFillColumnData(!fillColumnData)}
                        />
                        {`${t('Trūkstamus duomenis įkelti iš duomenų bazės')} `}
                      </div>

                      <div style={{ display: 'inline-block', marginRight: 5, fontWeight: '500' }}>
                        {t('Priskirti tipą visoms eilutėms')}:
                      </div>
                      <div style={{ display: 'inline-block', marginRight: 7 }}>
                        {renderSelect({
                          // name: col.key,
                          value: '',
                          onChange: (e) => handleMassTypeChange(e.target.value),
                          options: rowTypes,
                          disabled: !result,
                        })}
                      </div>
                    </div>
                  )}
                />
              </div>

              <div style={{ textAlign: 'right', marginTop: 20 }}>
                {!createEstimate &&
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleGetRowTypes}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    >
                      {t('Pritaikyti eilučių tipus')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleSelectedRowExcelDownload}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    >
                      {t('Atsisiųsti')}
                    </Button>
                  </>
                }
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={!createEstimate ? handleSubmit : handleNewEstimateSubmit}
                  disabled={loading}
                >
                  {t('Įkelti')}
                </Button>
              </div>
            </div>
          </>
        )}

      </div>

    </CommonDialog>
  )
}

export default RatesImportModal;